import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useThematicPackages } from '@features/campaigns/shared/hooks/useThematicPackages.ts';
import {
  CUSTOM_BUDGET_TIER_CODE,
  ThematicPackageBudgetTierId,
  ThematicPackageUpsellNavigationState,
} from '@features/campaigns/shared/types/campaignSubmit.ts';
import { UpsellPhase, UpsellPhases } from '@features/campaigns/thematicPackage/consts/upsellPhases.ts';
import {
  buildThematicPackageUpsellValidationSchema,
  thematicPackageUpsellDefaultValues,
  ThematicPackageUpsellFields,
  ThematicPackageUpsellFormValues,
} from '@features/campaigns/thematicPackage/containers/ThematicPackageUpsellPage/ThematicPackageUpsellPage.types.ts';
import { useCreateThematicPackageCampaign } from '@features/campaigns/thematicPackage/hooks/useCreateThematicPackageCampaign.tsx';
import { buildBudgetTierCards } from '@features/campaigns/thematicPackage/utils/buildBudgetTierCards.ts';
import {
  isCustomBudgetAmountWithinBounds,
  resolveCustomBudgetBounds,
} from '@features/campaigns/thematicPackage/utils/resolveCustomBudgetBounds.ts';
import { resolveLowestBudgetAmount } from '@features/campaigns/thematicPackage/utils/resolveLowestBudgetAmount.ts';
import { routePaths } from '@shared/router/routePaths.ts';

export function useThematicPackageUpsell() {
  const { campaignId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const navigationState = state as ThematicPackageUpsellNavigationState | null;

  const isUpsellEntryAllowed = Boolean(campaignId) && Boolean(navigationState?.isUpsellFlowEntryAllowed);

  const [upsellPhase, setUpsellPhase] = useState<UpsellPhase>(UpsellPhases.BUDGET);

  const customBudget = navigationState?.customBudget;
  const budgetTiers = buildBudgetTierCards(navigationState?.budgetTiers ?? [], customBudget);
  const lowestBudgetAmount = resolveLowestBudgetAmount(navigationState?.budgetTiers, customBudget);

  const customBudgetBounds = useMemo(
    () => resolveCustomBudgetBounds(navigationState?.budgetTiers, customBudget),
    [navigationState?.budgetTiers, customBudget],
  );

  const validationSchema = useMemo(
    () => buildThematicPackageUpsellValidationSchema(customBudgetBounds),
    [customBudgetBounds],
  );

  const form = useForm<ThematicPackageUpsellFormValues>({
    defaultValues: thematicPackageUpsellDefaultValues,
    resolver: zodResolver(validationSchema),
  });

  const selectedBudgetTierId = useWatch({
    control: form.control,
    name: ThematicPackageUpsellFields.BUDGET_TIER_ID,
  });
  const selectedCustomBudgetAmount = useWatch({
    control: form.control,
    name: ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT,
  });
  const selectedThematicPackageId = useWatch({
    control: form.control,
    name: ThematicPackageUpsellFields.THEMATIC_PACKAGE_ID,
  });

  const isPackagesPhase = upsellPhase === UpsellPhases.PACKAGES;
  const isCustomBudgetSelected = selectedBudgetTierId === CUSTOM_BUDGET_TIER_CODE;
  const isBudgetSelectionComplete =
    Boolean(selectedBudgetTierId) &&
    (!isCustomBudgetSelected || isCustomBudgetAmountWithinBounds(selectedCustomBudgetAmount, customBudgetBounds));

  const { thematicPackages, areThematicPackagesLoading } = useThematicPackages({
    isEnabled: isUpsellEntryAllowed && isPackagesPhase,
  });

  const { createThematicPackageCampaignMutation, isCreateThematicPackageCampaignPending } =
    useCreateThematicPackageCampaign({ sourceCampaignId: campaignId ?? '' });

  const navigateToCampaignsList = () => {
    void navigate(routePaths.campaigns, { replace: true });
  };

  useEffect(() => {
    if (isUpsellEntryAllowed) {
      return;
    }

    void navigate(routePaths.campaigns, { replace: true });
  }, [isUpsellEntryAllowed, navigate]);

  const handleProceedToPackages = async () => {
    const isBudgetValid = await form.trigger([
      ThematicPackageUpsellFields.BUDGET_TIER_ID,
      ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT,
    ]);

    if (!isBudgetValid) {
      return;
    }

    setUpsellPhase(UpsellPhases.PACKAGES);
  };

  const handleBackToBudget = () => setUpsellPhase(UpsellPhases.BUDGET);

  const handleCreateThematicPackageCampaign = form.handleSubmit(
    ({ budgetTierId, customBudgetAmount, thematicPackageId }) => {
      if (budgetTierId === CUSTOM_BUDGET_TIER_CODE) {
        if (customBudgetAmount === null) {
          return;
        }

        createThematicPackageCampaignMutation({
          thematicPackageId,
          budget: { tierCode: CUSTOM_BUDGET_TIER_CODE, amount: customBudgetAmount },
        });

        return;
      }

      createThematicPackageCampaignMutation({
        thematicPackageId,
        budget: { tierCode: budgetTierId as ThematicPackageBudgetTierId },
      });
    },
  );

  return {
    form,
    upsellPhase,
    isPackagesPhase,
    isUpsellEntryAllowed,
    budgetTiers,
    customBudgetBounds,
    lowestBudgetAmount,
    thematicPackages,
    areThematicPackagesLoading,
    isBudgetSelectionComplete,
    isCustomBudgetSelected,
    selectedThematicPackageId,
    isCreateThematicPackageCampaignPending,
    handleProceedToPackages,
    handleBackToBudget,
    handleCreateThematicPackageCampaign,
    handleSkipUpsell: navigateToCampaignsList,
    handleCloseUpsell: navigateToCampaignsList,
  };
}
